export const meta = {
  id: '40-participles',
  order: 40,
  titleFi: 'Partisiipit: kun verbistä tulee adjektiivi',
  titleEn: 'Participles',
  lemmaIds: [
    'urnēbagon', 'urnēbire', 'gīmije', 'rhēdessiarza', 'jaqagon', 'jaqiarza',
    'liorilaksa', 'ruaragon', 'gaomilaksir', 'massigon', 'ēdrȳñagon', 'qilōnagon', 'ozbaragon',
    'morghūlilare', 'nāmorghūlilare', 'hōzigon', 'hōzire', 'ēdrȳnta', 'sikagon',
    'ours:dokirare',
    'ours:deskriptioni',
    'ours:ēbrīha',
    'ours:dorojenity',
  ],
  exampleIds: [
    'hotd-s3e07-003', 'hotd-s1e05-004', 'got-s5e04-003', 'got-s4e06-016', 'got-s7e02-003',
    'hotd-s1e10-007', 'ex-5392f63a5a8c', 'ex-45bae5a27c28', 'ex-99544e0b8bc0', 'got-s3e06-005',
    'got-s7e02-006', 'hotd-s1e07-001', 'ex-10d381016267',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Conjugation',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Derivational_Affixes',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Adjectives',
    'https://wiki.languageinvention.com/index.php?title=Gaomilaksir',
    'https://wiki.languageinvention.com/index.php?title=Nāmorghūlilare',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['participles'],
  drills: { nonFinite: ['participle', 'present-participle', 'perfect-participle'] },
  showLines: ['hotd-s3e07-003', 'hotd-s1e05-004', 'got-s5e04-003', 'got-s4e06-016', 'got-s7e02-003', 'hotd-s1e10-007', 'got-s3e06-005', 'got-s7e02-006', 'hotd-s1e07-001'],
  pronunciation: { focus: 'stress', words: ['urnēbagon', 'urnēbire', 'gīmije', 'rhēdessiarza', 'jaqagon'], lines: ['got-s5e04-003', 'got-s4e06-016', 'got-s7e02-003'] },
  letter: {
    fi: 'Kuvaile ihmisiä tai lohikäärmeitä partisiipeilla.',
    en: 'Describe people or dragons with participles.',
  },
  scene: 'got-s5e04-1',
}

# Partisiipit: kun verbistä tulee adjektiivi

Tämä on harvinainen oppitunti: tässä suomesta on teille **etua**. Suomessa on runsaasti partisiippeja –
*syövä, syönyt, syötävä, syöty* ja agenttipartisiippi *syömä* – ja käytätte niitä joka päivä
ajattelematta. Korkeavalyriassa on samanlainen järjestelmä, vain hieman eri tavalla jaoteltu.

Wikin kielioppisivun määritelmä: partisiipit ovat verbimuotoja, jotka ovat **muodoltaan adjektiiveja**
ja määrittävät substantiiveja osana partisiippilauseketta. Niitä on useimmille aikamuodon ja
pääluokan yhdistelmille, mutta ei kaikille.

## Partisiippien kartta

Tässä yhden verbin kaikki partisiipit:

<Paradigm lemma="ipradagon" filter="participle" />

Ja sama suomen näkökulmasta:

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Valyria</th><th>Muodostus</th><th>Taipuu kuin</th><th>Lähin suomen vastine</th></tr></thead>
  <tbody>
    <tr><td>preesensin aktiivi</td><td><Hv>-re</Hv></td><td>II-luokan adjektiivi</td><td><em>syövä</em> (-va)</td></tr>
    <tr><td>aoristin aktiivi</td><td><Hv>-ire</Hv> / vokaalivartalo <Hv>-je</Hv></td><td>II-luokka</td><td><em>syövä</em> tapana, taitona</td></tr>
    <tr><td>futuurin aktiivi</td><td><Hv>-ilare</Hv></td><td>II-luokka</td><td><em>tuleva</em>, <em>syömäisillään</em></td></tr>
    <tr><td>perfektin partisiippi</td><td>perfektivartalo + <Hv>-a</Hv></td><td>I-luokka</td><td><em>syöty</em> tai <em>nukkunut</em></td></tr>
    <tr><td>aoristin passiivi</td><td><Hv>-iarza</Hv></td><td>I-luokka</td><td><em>syöty</em> yleisesti: <em>rakastettu</em></td></tr>
    <tr><td>futuurin passiivi</td><td><Hv>-ilaksa</Hv></td><td>I-luokka</td><td><em>syötävä</em> (-tava)</td></tr>
    <tr><td><Term id="mennyt-toistuva">menneen toistuvan</Term> aktiivi ja passiivi</td><td>perfektivartalo + <Hv>-re</Hv> / <Hv>-iarza</Hv></td><td>II- / <Term id="adjektiiviluokka">I-luokka</Term></td><td>ei vastinetta (<LessonLink n={41}>oppitunti 41</LessonLink>)</td></tr>
  </tbody>
</table>
</div>

Wikin verbintaivutussivu toteaa erikseen, että **preesensin passiivin partisiippia ei ole**. Suomen *syötävä*
vastaa siis valyrian futuurin passiivia, ei preesensiä – ja sehän viittaa suomessakin
tulevaan: ”syötävä” = sellainen, joka tullaan syömään.

## Aktiivin partisiipit

### Preesens: *-re*

Preesensin aktiivin partisiippi on <Term id="adjektiiviluokka">II-luokan</Term> adjektiivi: kuu- ja aurinkosuvussa *-re*, maa- ja
vesisuvussa *-rior* (taivutussivun esimerkit <Hv l="jaelagon">jaelare</Hv> ~ <Hv fi="haluava">jaelarior</Hv>, <Hv>issare</Hv> ~
<Hv fi="oleva">issarior</Hv>). Daenerys perustelee, miksi vankia ei olisi saanut tappaa:

<Line id="got-s5e04-003" />

<Interlinear hv="Īlva ozgūroty īles, iderennī jumb|are." gloss="meidän-LUN.NOM vanki-NOM olla-IPFV.3SG oikeudenkäynti-ACC odottaa-PRS.PTCP" fi="Hän oli vankimme, oikeudenkäyntiä odottava." />

<Compare fi="odotta|va" hv="jumb|are" label="preesensin aktiivin partisiippi" />

Huomatkaa järjestys: partisiipin täydennys (<Hv glossed>iderennī</Hv> ’oikeudenkäyntiä’) tulee **ennen**
partisiippia – aivan kuten suomen *oikeudenkäyntiä odottava*. Valyriassa verbi tulee lauseen loppuun, ja
partisiippi käyttäytyy kuin pienen lausekkeen loppuun sijoittuva verbi.

Partisiippi taipuu kuten adjektiivi. Tässä esimerkkinä preesensin partisiipin <Hv glossed>hōzire</Hv> ’turpoava’
taivutus:

<Paradigm lemma="hōzire" cases="nom,acc,gen,dat" numbers="sg,pl" filter="positive.pre" />

Maa- ja vesisuvun muoto voi myös seistä substantiivin edellä. Petersonin esimerkki:

<Example id="ex-10d381016267" />

<Hv glossed>Ājehikarior</Hv> ’kimalteleva’ on preesensin partisiippi maasuvun muodossa, koska <Hv glossed>āeksȳndi</Hv>
’kulta’ on maasukuinen.

### Aoristi: *-ire*, *-je*

Aoristin partisiippi kertoo, millainen joku **on tavallisesti tai luonnostaan** – kuten aoristi
<LessonLink n={38}>oppitunnilla 38</LessonLink>. Siksi aoristin partisiipeista tulee helposti luonnehtivia adjektiiveja.
Konsonanttivartaloilla pääte on *-ire* (<Hv l="jaelagon">jaelire</Hv>), vokaalivartaloilla *-je*
(<Hv>ȳdraje</Hv>, <Hv>barduje</Hv>).

<Line id="hotd-s1e05-004" />

<Interlinear hv="Laenor sȳz vala se gīm|ije azantys issa." gloss="Laenor-NOM hyvä-LUN.NOM mies-NOM ja tietää-AOR.PTCP ritari-NOM olla-PRS.3SG" fi="Laenor on hyvä mies ja taitava ritari." />

<Hv l="gīmigon">Gīmije</Hv> on <Hv glossed>gīmigon</Hv>-verbin (’tietää, osata’) aoristin partisiippi: ’osaava’ – eli taitava.

Rhaenyra antaa käskyn:

<Line id="hotd-s3e07-003" />

<Interlinear hv="Urnēb|ire s|ās." gloss="valvoa-AOR.PTCP olla-IMP.2SG" fi="Ole valpas." />

Valpas on se, joka valvoo – aina, ei vain nyt.

### Futuuri: *-ilare*

Futuurin aktiivin partisiippi tarkoittaa ’tuleva, jotakin tekemäisillään oleva’: taivutussivun
esimerkki on <Hv glossed>jaelilare</Hv> ’haluamaisillaan’. Kauneimmin sen näkee sanoissa, jotka ovat jo
sanakirjassa omina hakusanoinaan: <Hv glossed>morghūlilare</Hv> ’kuolevainen’ (kirjaimellisesti ’joka tulee
kuolemaan’) ja <Hv glossed>nāmorghūlilare</Hv> ’kuolematon’.

<Compare l="morghūlilare" fi="kuole|vainen" hv="morghūl|ilare" label="futuurin aktiivin partisiippi" />

Sarjoissa futuurin partisiippi esiintyy aikaa koskevissa ilmauksissa. Melisandre:

<Line id="got-s7e02-006" />

<Hv glossed>Massīlaro vīlībāzmoti</Hv> ’tulevissa sodissa’ (kirjaimellisesti ’tapahtuvissa sodissa’) –
partisiippi taipuu sotien mukaan. Samoin Vaemond hautajaisissa: <Hv glossed>māzīlarē tubirri</Hv>
’kaikkina tulevina päivinä’:

<Line id="hotd-s1e07-001" />

## Perfektin partisiippi: *-ta*

Perfektin partisiippi muodostetaan perfektivartalosta, ja se on <Term id="adjektiiviluokka">I-luokan</Term> adjektiivi:
kuusuku *-a*, aurinkosuku *-ys*, maasuku *-on*, vesisuku *-or* (<Hv glossed>barduta</Hv>, <Hv glossed>bardutys</Hv>,
<Hv glossed>barduton</Hv>, <Hv glossed>bardutor</Hv>). Nyt tulee kiinnostava kohta:

- **transitiiviverbeillä** merkitys on passiivinen: <Hv glossed>barduta</Hv> ’kirjoitettu’,
- **intransitiiviverbeillä** aktiivinen: <Hv glossed>ēdruta</Hv> ’nukkunut’, <Hv glossed>morghūlta</Hv> ’kuollut’.

Suomessa on kaksi eri muotoa, *kirjoittanut* ja *kirjoitettu*. Valyriassa muoto on yksi, ja verbin
luonne ratkaisee: koska kuolla ei voi ”jotakuta”, *kuollut* on ainoa mahdollinen tulkinta.

Melisandre kertoo menneisyydestään Daenerysille:

<Line id="got-s7e02-003" />

<Interlinear hv="Dohaeriros istin, sindita liortā, qilōnta ozbartā." gloss="orja-NOM olla-PST.HAB.1SG ostaa-PRF.PTCP myydä-PRF.PTCP=ja ruoskia-PRF.PTCP polttomerkitä-PRF.PTCP=ja" fi="Olin kerran orja, ostettu ja myyty, ruoskittu ja polttomerkitty." />

(Pitkät loppuvokaalit sanoissa <Hv fi="ja myyty">liortā</Hv> ja <Hv glossed>ozbartā</Hv> ovat <Term id="konjunktiivinen-pidennys">konjunktiivista pidennystä</Term>: ne
tarkoittavat ’ja’. Perusmuodot ovat <Hv>liorta</Hv> ja <Hv>ozbarta</Hv>. Entä <Hv l="sagon">istin</Hv>? Se on
menneen toistuvan aikamuodon muoto – seuraavan oppitunnin aihe.)

Rhaenyra kuolleena syntyneestä tyttärestään:

<Line id="hotd-s1e10-007" />

<Hv glossed>Sitta</Hv> ’syntynyt’ on <Hv glossed>sikagon</Hv>-verbin (’synnyttää’) perfektin partisiippi –
kirjaimellisesti ’synnytetty’, koska verbi on transitiivinen.

Intransitiivinen tapaus tuli vastaan jo <LessonLink n={38}>oppitunnilla 38</LessonLink>: Rhaenyran <Hv glossed>ēdrȳnta</Hv> ’väsynyt’ on
<Hv glossed>ēdrȳñagon</Hv>-verbin (’väsyä’) perfektin partisiippi – ’väsynyt’, ei ’väsytetty’.

### Perifrastinen passiivi: *keliton issa*

<Example id="ex-5392f63a5a8c" />

<Interlinear hv="Sesīr urnēbion zȳhon keliton issa." gloss="nyt vartio-NOM hänen-TERR.NOM päättyä-PRF.PTCP olla-PRS.3SG" fi="Nyt hänen vartionsa on päättynyt." />

Muistatko Yövartion hautajaissanat <LessonLink n={20}>oppitunnilta 20</LessonLink>? Silloin opettelit ne kokonaisina. Nyt näet, miksi ’on päättynyt’ on niissä kaksi sanaa: partisiippi ja olla-verbi.

Tämä on Yövartion hautajaissanojen valyriankielinen käännös. Peterson kertoo (wikin
taivutussivun lainaamana) valinneensa partisiipin ja olla-verbin, jotta käännös muistuttaisi
englannin vanhahtavaa *is ended* -muotoa; saman olisi voinut sanoa yhdellä taivutetulla verbillä.
Rakenne on siis mahdollinen mutta harvinainen – tavallisesti passiivi tehdään *-ks*-päätteellä.
Huomatkaa myös <Hv l="keligon" fi="päättynyt">keliton</Hv>: maasuvun muoto, koska <Hv glossed>urnēbion</Hv> ’vartio’ on maasukuinen.

## Passiivin partisiipit

### Aoristin passiivi: *-iarza*

Aoristin passiivin partisiippi kertoo ominaisuudesta, joka johtuu siitä, mitä toiset tavallisesti
tekevät: ’kunnioitettu’, ’rakastettu’. Wikin esimerkkejä: <Hv glossed>jorrāeliarza</Hv> ’rakas’,
<Hv glossed>raqiarza</Hv> ’rakastettu’. Kaksi tuttua sarjasta:

<Line id="got-s4e06-016" />

<Hv glossed>Rhēdessiarza</Hv> ’jalosukuinen’ tulee verbistä <Hv glossed>rhēdegon</Hv> ’tuntea (henkilö)’: kirjaimellisesti
’tunnettu, sellainen, jonka kaikki tuntevat’. <LessonLink n={38}>Oppitunnilla 38</LessonLink> kuulitte areenan taistelijan huutavan
<Hv glossed>jaqiarzus Dārȳs</Hv> ’oi kunniakas kuningatar’ – <Hv glossed>jaqiarza</Hv> on verbin <Hv>jaqagon</Hv>
aoristin passiivin partisiippi. Sanakirja ei anna sille omaa merkitystä, joten kirjaimellisesti se on
’täydennetty’. ’Kunniakas’ tulee repliikin englanninkielisestä käännöksestä (*glorious*), ja samasta
partisiipista on johdettu <Hv glossed>jaqiarzir</Hv> ’kunnia’.

Se taipuu kuten tavallinen <Term id="adjektiiviluokka">I-luokan</Term> adjektiivi:

<Paradigm lemma="jaqiarza" cases="nom,acc,gen,dat,voc" numbers="sg,pl" filter="positive.pre.lunar" />

### Futuurin passiivi: *-ilaksa* = suomen *-tava*

Tämä on ehkä mukavin vastaavuus koko kurssilla.

<Example id="ex-45bae5a27c28" />

<Interlinear hv="Brāedāzma lior|ilaksa iksos daor." gloss="pronssi-NOM myydä-FUT.PASS.PTCP olla-PRS.SBJV.3SG ei" fi="Pronssi ei ole myytävänä." />

<Compare l="liorilaksa" fi="myy|tävä" hv="lior|ilaksa" label="futuurin passiivin partisiippi" />

Taivutussivu huomauttaa, että vaikka muoto merkitsee kirjaimellisesti ’joka tullaan tekemään’, sen
käyttö on laajempaa: <Hv glossed>liorilaksa</Hv> ’myytävä’ ja substantiiviksi muuttunut <Hv glossed>gaomilaksir</Hv>
’tehtävä’. Suomen *tehtävä* on täsmälleen sama johdos: *tehdä* → *tehtävä* ’se, mikä on tehtävä’.

<Line id="got-s3e06-005" />

<Example id="ex-99544e0b8bc0" />

<Hv l="ruarilaksa" glossed>Ruarilaksa</Hv> on <Hv glossed>ruaragon</Hv>-verbin (’kätkeä’) futuurin passiivin partisiippi:
’kätkettävä’ – siis yksityinen, salattava.

## Kielteinen partisiippi

Partisiippia ei voi kieltää sanalla <Hv>daor</Hv> (sillä ei ole subjunktiivia). Wikin mukaan
kielteinen merkitys ilmaistaan joko sivulauseella tai etuliitteellä. Sanakirjasta löytyy
<Hv glossed>nāmorghūlilare</Hv> ’kuolematon’ ja wikin johdossivulta <Hv glossed>Dovaogēdy</Hv>
’Tahraton, Tahrattomien jäsen’ = *do-* + <Hv glossed>vaogēdy</Hv> ’tahrattu (henkilö)’, joka on johdettu
<Hv glossed>vaogemagon</Hv>-verbin perfektin partisiipista <Hv glossed>vaogēda</Hv> ’tahrattu’.

<Note>
**Partisiipista substantiivi.** Wikin sanakirjassa moni substantiivi on johdettu partisiipista: <Hv glossed>tymptir</Hv> ’peli’ ja <Hv glossed>vestretir</Hv> ’sanonta’ perfektin partisiipeista *tympta* ja *vestreta*, <Hv glossed>dohaeriros</Hv> ’orja’ aoristin partisiipista *dohaerire*. Damien Halme (Academy of High Valyrian) on huomannut, että konkreettiset, laskettavat sanat ovat kuu- tai aurinkosukua (<Hv glossed>tyvaros</Hv> ’käärme’, <Hv>dohaeriros</Hv>) ja abstraktit maa- tai vesisukua (<Hv>vestretir</Hv>, <Hv glossed>gūrotrir</Hv> ’ansio’). Se on havainto, ei sääntö, mutta auttaa arvaamaan uuden sanan suvun.
</Note>

## Harjoituslauseita

<Practice hv="Ziry gīmije azantys issa." fi="Hän on taitava ritari." />

<Practice hv="Lenton liorilakson issa." fi="Talo on myytävänä." />

<Practice hv="Urnēbiri sātās!" fi="Olkaa valppaita!" />

<Practice hv="Valar morghūlilare issa." fi="Kaikki ihmiset ovat kuolevaisia. (Kollektiivi saa verbin yksikköön.)" />

## Muistilista

- Aktiivi: *-re* (nyt), *-ire/-je* (aina, luonteeltaan), *-ilare* (tuleva).
- Perfektin *-ta*: passiivinen transitiiviverbeillä, aktiivinen intransitiiviverbeillä.
- Passiivi: *-iarza* (yleisesti tehty), *-ilaksa* (tehtävä = suomen *-tava*).
- Partisiippi taipuu kuin adjektiivi, ja sen täydennys tulee sen eteen – kuten suomessa.

## Sanasto

<Vocab />

## Tarkistus

<Check />
